import { useCallback, useRef } from "react";
import { shouldCaptureTextAsAsyncCareSource } from "../features/careSourceProcessing/sourceFirstRouting";
import { notifyComposerSaved } from "../utils/composerSaveEvents";

// Only an acknowledged source save or the modal's completion may reset a draft.
export default function useComposerCaptureSubmit({
  captureSource,
  openQuickEntry,
  setSaving,
  setSavingMode,
  onFailure,
}) {
  const pendingChild = useRef(null);
  const cancel = useCallback(() => {
    pendingChild.current = null;
    setSaving(false);
  }, [setSaving]);
  const fail = useCallback(() => {
    setSaving(false);
  }, [setSaving]);
  const complete = useCallback(() => {
    const childId = pendingChild.current;
    pendingChild.current = null;
    setSaving(false);
    if (childId) notifyComposerSaved(childId);
  }, [setSaving]);
  const submit = useCallback(
    async ({
      actionKey = "quick_note",
      child,
      event,
      captureDate,
      text = "",
      photoFile = null,
      photoIntent = "",
    }) => {
      if (!child?.id || pendingChild.current) return;
      pendingChild.current = child.id;
      const sourceFirst = shouldCaptureTextAsAsyncCareSource({
        text: String(text).trim(),
        photoFile,
      });
      setSavingMode(sourceFirst ? "source" : "note");
      setSaving(true);
      try {
        if (sourceFirst) {
          const saved = await captureSource({
            child,
            text: String(text).trim(),
            captureDate,
            inputMode: "composer",
            sourceName: "Typed note",
          });
          if (saved) {
            complete();
            return;
          }
          // Preserve the existing modal fallback; only its eventual acknowledgement
          // can clear the composer or reset its selected day.
        }
        openQuickEntry(
          child,
          actionKey,
          event,
          captureDate || null,
          text,
          null,
          photoFile,
          { autoSubmit: true, photoIntent },
        );
      } catch (error) {
        cancel();
        onFailure?.(error);
      }
    },
    [
      captureSource,
      openQuickEntry,
      setSaving,
      setSavingMode,
      complete,
      cancel,
      onFailure,
    ],
  );
  return { submit, complete, cancel, fail };
}
